import {
    CreditCard,
    Calendar,
    CheckCircle,
    AlertCircle,
    ArrowRight,
    Clock,
    Loader2,
} from 'lucide-react';
import { useAccountBilling } from '../../../Hooks/useAccountBilling';
import { format } from 'date-fns';

export default function AccountBilling() {
    const {
        paymentHistory,
        isTrial,
        activeSince,
        trialEndDate,
        planName,
        planPrice,
        isLoading,
        error,
    } = useAccountBilling();

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-teal-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-10 text-center text-red-600">
                Failed to load account information. Please try again later.
            </div>
        );
    }

    return (
        <section className="min-h-screen  text-sm">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Account and Billing</h1>
                        <p className="text-xs text-gray-600 mt-1">
                            Manage your subscription, payments, and account status
                        </p>
                    </div>
                </div>
            </div>

            <div className="px-4 py-5 space-y-5">
                {/* Account Overview */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-teal-600" />
                        Account Overview
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Active Since */}
                        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-500" />
                                <span className="text-xs font-medium text-gray-700">Active since</span>
                            </div>
                            <span className="text-xs font-medium text-gray-900">{activeSince}</span>
                        </div>

                        {/* Account Status */}
                        <div className="flex items-center justify-between py-2 px-3 bg-emerald-50 rounded-lg">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                                <span className="text-xs font-medium text-gray-700">Account status</span>
                            </div>
                            <span className="text-xs font-medium text-emerald-700">
                                {isTrial ? 'Trial' : 'Active'}
                            </span>
                        </div>

                        {/* Current Plan */}
                        <div className="flex items-center justify-between py-2 px-3 bg-amber-50 rounded-lg col-span-2">
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-amber-600" />
                                <span className="text-xs font-medium text-gray-700">Current plan</span>
                            </div>
                            <div className="text-right">
                                <span className="text-xs font-bold text-amber-700">{planName}</span>
                                {isTrial && trialEndDate && (
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Trial ends on {trialEndDate}
                                    </p>
                                )}
                                {!isTrial && (
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {planPrice} / month
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Trial / Billing Alert */}
                    {isTrial ? (
                        <div className="mt-4 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-teal-600 mt-0.5" />
                                <div>
                                    <p className="text-xs font-medium text-teal-900">Billing details</p>
                                    <p className="text-xs text-teal-700 mt-1">No credit card on file</p>
                                    <p className="text-xs text-teal-600 mt-1">
                                        Continue using the platform by choosing a plan before your trial ends.
                                    </p>
                                    <button className="mt-3 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg text-xs font-bold shadow-sm hover:shadow flex items-center gap-1.5">
                                        Choose Plan
                                        <ArrowRight className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                            <p className="text-xs font-medium text-emerald-800">
                                You are on the <strong>{planName}</strong> plan — {planPrice}/month
                            </p>
                        </div>
                    )}
                </div>

                {/* Payment History */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-teal-600" />
                        Payment History
                    </h2>

                    {paymentHistory.length === 0 ? (
                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                            <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16 mx-auto mb-3" />
                            <p className="text-xs font-medium text-gray-700">No payment history</p>
                            <p className="text-xs text-gray-500 mt-1">
                                Your payments will appear here once you subscribe.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {paymentHistory.map((payment) => (
                                <div
                                    key={payment.id}
                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                                >
                                    <div>
                                        <p className="text-xs font-medium text-gray-900">
                                            {payment.plan.title}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {format(new Date(payment.start_date), 'MMM d, yyyy')}
                                        </p>
                                    </div>
                                    <p className="text-sm font-bold text-gray-900">
                                        ${parseFloat(payment.price).toFixed(2)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Cancel Account */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-bold text-gray-900 mb-4">
                        Cancel your account
                    </h2>

                    <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-700">
                            {isTrial
                                ? 'Your trial will automatically expire, so there is no need to cancel.'
                                : 'You can cancel your subscription at any time from your plan settings.'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            After cancellation, you will still have access until the end of your billing period.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}